import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';

const CATEGORY_LABELS = {
  update: 'アップデート',
  feature: '新機能',
  bugfix: '不具合・修正',
  alert: 'アラート',
  maintenance: 'メンテナンス',
  announcement: 'お知らせ',
  notice: '告知',
  billing: '決済'
};

const toTimestampMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (typeof ts._seconds === 'number') return ts._seconds * 1000;
  const ms = new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const formatTs = (ts) => {
  const ms = toTimestampMs(ts);
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const TABS = [
  { key: 'global', label: '全体' },
  { key: 'individual', label: '個別' }
];

const NotificationModal = ({ notifications, uid, onClose, onMarkRead }) => {
  const [activeTab, setActiveTab] = useState('global');
  const [expandedId, setExpandedId] = useState(null);
  const [closing, setClosing] = useState(false);
  const tabsRef = useRef(null);
  const listInnerRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [listHeight, setListHeight] = useState(null);
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const readyRef = useRef(false);

  useLayoutEffect(() => {
    if (!tabsRef.current) return;
    const activeEl = tabsRef.current.querySelector('.hub-notif-tab.active');
    if (activeEl) {
      const containerRect = tabsRef.current.getBoundingClientRect();
      const tabRect = activeEl.getBoundingClientRect();
      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width
      });
    }
  }, [activeTab]);

  useEffect(() => {
    const el = listInnerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setListHeight(entry.contentRect.height);
      }
      if (!readyRef.current) {
        readyRef.current = true;
        setTimeout(() => setTransitionEnabled(true), 350);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const filtered = useMemo(() => {
    return notifications
      .filter((n) => n.type === activeTab)
      .slice()
      .sort((a, b) => (
        toTimestampMs(b.publishedAt || b.createdAt) - toTimestampMs(a.publishedAt || a.createdAt)
      ));
  }, [notifications, activeTab]);

  const unreadCounts = useMemo(() => ({
    global: notifications.filter(n => n.type === 'global' && !n.isRead).length,
    individual: notifications.filter(n => n.type === 'individual' && !n.isRead).length
  }), [notifications]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const handleExpand = (n) => {
    if (expandedId === n.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(n.id);
    if (!n.isRead) {
      onMarkRead(n.id);
    }
  };

  const headerTabsHeight = 130;
  const heightBuffer = 32;
  const maxListH = typeof window !== 'undefined'
    ? window.innerHeight * 0.8 - headerTabsHeight
    : 400;
  const effectiveHeight = listHeight !== null
    ? Math.min(listHeight + heightBuffer, maxListH)
    : undefined;

  const listOuterStyle = effectiveHeight !== undefined
    ? { height: effectiveHeight }
    : {};

  return (
    <div className={`hub-modal-overlay hub-notification-modal-overlay${closing ? ' closing' : ''}`} onClick={handleClose}>
      <div className="hub-notification-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="hub-notif-modal-header">
          <h2>お知らせ</h2>
          <button className="hub-notif-modal-close" onClick={handleClose} aria-label="閉じる">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="hub-notif-tabs" ref={tabsRef}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`hub-notif-tab${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {unreadCounts[tab.key] > 0 && (
                <span className="hub-notif-tab-badge">{unreadCounts[tab.key]}</span>
              )}
            </button>
          ))}
          <span
            className="hub-notif-tab-indicator"
            style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
          />
        </div>

        {/* List */}
        <div
          className={`hub-notif-list${transitionEnabled ? ' hub-notif-list--animated' : ''}`}
          style={listOuterStyle}
        >
          <div ref={listInnerRef}>
            <div key={activeTab} className="hub-notif-list-content">
              {filtered.length === 0 ? (
                <div className="hub-notif-empty">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 48, height: 48, color: 'var(--hub-text-muted)' }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  <p>お知らせはありません</p>
                </div>
              ) : (
                filtered.map(n => {
                  const isExpanded = expandedId === n.id;
                  return (
                    <div
                      key={n.id}
                      className={`hub-notif-item${n.isRead ? ' read' : ''}${isExpanded ? ' expanded' : ''}`}
                      onClick={() => handleExpand(n)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpand(n); } }}
                    >
                      <div className="hub-notif-item-row">
                        <span className={`hub-notif-cat-badge cat-${n.category || 'update'}`}>
                          {CATEGORY_LABELS[n.category] || n.category || 'お知らせ'}
                        </span>
                        {!n.isRead && <span className="hub-notif-unread-dot" />}
                        <span className="hub-notif-item-title">{n.title}</span>
                        <span className="hub-notif-item-date">{formatTs(n.publishedAt || n.createdAt)}</span>
                      </div>

                      {isExpanded && (
                        <div className="hub-notif-item-detail">
                          <div className="hub-notif-item-body" dangerouslySetInnerHTML={{ __html: (n.body || '').replace(/\n/g, '<br/>') }} />
                          {n.meta?.actionUrl && (
                            <a
                              className="hub-notif-action-btn"
                              href={n.meta.actionUrl}
                              target={n.meta.actionUrl.startsWith('http') ? '_blank' : undefined}
                              rel={n.meta.actionUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
                              onClick={e => e.stopPropagation()}
                            >
                              {n.meta.actionLabel || 'リンクを開く'}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14, marginLeft: 4 }}>
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                            </a>
                          )}
                          {n.readAt && (
                            <div className="hub-notif-read-info">
                              既読: {formatTs(n.readAt)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
